import { Component, EventEmitter, Input, Output, OnInit, ViewChild, ElementRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PassportDetailsComponent } from '../passport-details/passport-details.component';
import { Router } from '@angular/router';
import { PassportDetailsStore, type PassportDetails } from '../shared/passport-details.store';
import { UiStateService } from '../shared/ui-state.service';
import { OfflineStorageService } from '../shared/offline-storage.service';
import { CommentsPanelService } from '../shared/comments-panel.service';
import { HistoryPanelService } from '../shared/history-panel.service';
import { DateTimeHelperService } from '../shared/date-time-helper.service';
import { ScrollPositionService } from '../shared/scroll-position.service';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { ToastService } from '../shared/toast.service';

type OutgoingPassportSubmission = { data: Partial<PassportDetails> };

function isOutgoingPassportSubmission(value: unknown): value is OutgoingPassportSubmission {
  return typeof value === 'object' && value !== null && 'data' in value;
}

@Component({
  selector: 'app-passport-cell',
  standalone: true,
  imports: [CommonModule, PassportDetailsComponent],
  templateUrl: './passport-cell.component.html',
  styleUrl: './passport-cell.component.css'
})
export class PassportCellComponent implements OnInit {
  @Input() recordId = '';
  @Input() loadNumber = '';
  @Input() scheduledText = '';
  @Input() plannedText = '';
  @Input() plannedDateTime = '';
  @Input() tags: string[] = [];
  @Input() origin = '';
  @Input() destination = '';
  @Input() apn = '';
  @Input() apnName = '';
  @Input() vendorName = '';
  @Input() vendorNumber = '';
  @Input() bulkerNumber = '';
  @Input() status: 'pending' | 'outgoing' | 'completed' = 'pending';
  @Input() showPlannedSection = true;
  // Visual state for special styling variants that don't affect filtering logic
  @Input() styleState: 'default' | 'pcr-change' | 'cancelled' | 'state-2' = 'default';

  @Output() expand = new EventEmitter<void>();

  @ViewChild('dateInput') dateInputRef!: ElementRef<HTMLInputElement>;

  isExpanded = false;
  plannedOpen = false;
  plannedLoadingValue = 'Select';
  plannedLoadingISO = '';
  plannedOptions: string[] = ['Morning', 'Afternoon', 'Evening'];
  infoVisible = false;
  private infoTimer: any;

  private readonly offlineStorage = inject(OfflineStorageService);
  private readonly commentsPanelService = inject(CommentsPanelService);
  private readonly historyPanelService = inject(HistoryPanelService);
  private readonly dateTimeHelper = inject(DateTimeHelperService);
  private readonly scrollPositionService = inject(ScrollPositionService);
  private readonly toastService = inject(ToastService);

  constructor(private readonly router: Router, private readonly details: PassportDetailsStore, private readonly ui: UiStateService, private readonly http: HttpClient) {}

  ngOnInit(): void {
    const id = this.recordId || this.loadNumber || '';
    if (this.ui.expandedRecordId() === id) {
      this.isExpanded = true;
    }

    // Initialize Planned Loading display and input from provided plannedDateTime
    const iso = (this.plannedDateTime || '').trim();
    if (iso) {
      this.plannedLoadingISO = this.toInputLocal(iso);
      this.plannedLoadingValue = this.formatDisplay(iso);
    } else {
      this.plannedLoadingISO = '';
      this.plannedLoadingValue = 'Select';
    }
  }

  onToggleExpand(): void {
    const id = this.recordId || this.loadNumber || '';
    const shouldExpand = !(this.ui.expandedRecordId() === id && this.isExpanded);
    this.isExpanded = shouldExpand;
    this.expand.emit();
    if (this.isExpanded) this.ui.setExpandedRecord(id); else if (this.ui.expandedRecordId() === id) this.ui.setExpandedRecord('');
  }

  async openDetails(): Promise<void> {
    const id = this.recordId || this.loadNumber || 'unknown';
    // Fetch data from API if online, or use cached data if offline
    await this.fetchAndStorePassportDetails(id);
    
    // Don't check images here - the passport details page will handle image loading and checking
    // Images haven't been loaded yet at this point, so checking now would give false positives
    
    this.ui.setExpandedRecord(id);
    // Save scroll position and set returning flag before navigating
    this.scrollPositionService.saveScrollPosition('/growers');
    this.scrollPositionService.setReturningFromDetail(true);
    void this.router.navigate(['/passport', id], { state: { from: 'growers' } });
  }

  private async fetchAndStorePassportDetails(passportId: string): Promise<void> {
    // First, check if this is an outgoing passport with saved submission data
    // This ensures modified images (deleted/new) are restored correctly
    const outgoingData = await this.loadOutgoingPassportData(passportId);
    if (outgoingData && typeof outgoingData === 'object') {
      this.restoreOutgoingPassportData(passportId, outgoingData);
      return;
    }

    // Check if data is already in store (from main list API)
    const existingData = this.details.get(passportId);
    if (existingData) {
      await this.handleExistingData(passportId, existingData);
      return;
    }

    // Data not in store - try loading from offline cache
    console.log('📦 Passport details not in store, checking offline cache for', passportId);
    await this.loadFromOfflineCache(passportId);
  }

  /**
   * Restore outgoing passport data from saved submission
   */
  private restoreOutgoingPassportData(passportId: string, outgoingData: unknown): void {
    if (!isOutgoingPassportSubmission(outgoingData)) {
      return;
    }

    const payload = outgoingData.data;
    // Also check if the payload is present (handles undefined case)
    if (!payload) {
      return;
    }

    console.log('📤 Found outgoing passport submission data, restoring modified images');
    // Restore the passport details from saved submission data
    this.details.upsert({
      id: passportId,
      ...payload,
      // Ensure images are restored correctly
      haccpImages: payload.haccpImages ?? [],
      haccpImageFiles: payload.haccpImageFiles ?? [],
      deletedImageFilenames: payload.deletedImageFilenames ?? []
    });
  }

  /**
   * Handle existing data in store - load missing comments/history if offline
   */
  private async handleExistingData(passportId: string, existingData: any): Promise<void> {
    // Even if data exists, check if comments/history are missing and try to load them from IndexedDB
    // Only do this if offline - when online, components will fetch from API
    const numericId = Number.parseInt(passportId, 10);
    const isValidNumericId = Number.isFinite(numericId);
    const hasMissingData = !existingData.comments || !existingData.history;
    
    if (isValidNumericId && hasMissingData) {
      await this.loadMissingCommentsAndHistory(passportId, numericId);
    } else {
      console.log('✅ Using passport details already in store for', passportId);
    }
  }

  /**
   * Load missing comments and history from IndexedDB when offline
   */
  private async loadMissingCommentsAndHistory(passportId: string, numericId: number): Promise<void> {
    const isOffline = navigator.onLine === false;
    if (isOffline) {
      console.log('📦 Passport details in store but comments/history missing, checking IndexedDB for', passportId);
      const cachedComments = await this.safeLoadComments(numericId);
      const cachedHistory = await this.safeLoadHistory(numericId);
      
      // Update detailsStore with comments/history if found
      if (cachedComments && cachedComments.length > 0) {
        this.details.update(passportId, { comments: cachedComments });
        console.log(`✅ Loaded ${cachedComments.length} comments from IndexedDB for passport ${passportId}`);
      }
      if (cachedHistory && cachedHistory.length > 0) {
        this.details.update(passportId, { history: cachedHistory });
        console.log(`✅ Loaded ${cachedHistory.length} history items from IndexedDB for passport ${passportId}`);
      }
    } else {
      console.log('✅ Using passport details already in store for', passportId, '- components will fetch from API when needed');
    }
  }

  /**
   * Load outgoing passport submission data if it exists
   */
  private async loadOutgoingPassportData(passportId: string): Promise<unknown> {
    try {
      return await this.offlineStorage.getOutgoingPassport(passportId);
    } catch (error) {
      console.warn('Failed to load outgoing passport data:', error);
      return null;
    }
  }

  /**
   * Load passport details from offline cache (IndexedDB)
   */
  private async loadFromOfflineCache(passportId: string): Promise<void> {
    try {
      const numericId = Number.parseInt(passportId, 10);
      if (Number.isNaN(numericId)) {
        console.warn('Invalid passport ID for cache lookup:', passportId);
        // Use minimal data from component props
        this.useMinimalFallbackData(passportId);
        return;
      }

      const cachedData = await this.offlineStorage.getPassport(numericId);
      
      if (cachedData) {
        console.log('📦 Loaded passport details from cache');
        
        // Also load cached comments and history (with error handling)
        const cachedComments = await this.safeLoadComments(numericId);
        const cachedHistory = await this.safeLoadHistory(numericId);
        
        // Store all available data
        // Determine HACCP check value
        let haccpCheckValue: 'Yes' | 'No' | undefined;
        if (cachedData.haccpCheck === true) {
          haccpCheckValue = 'Yes';
        } else if (cachedData.haccpCheck === false) {
          haccpCheckValue = 'No';
        } else {
          haccpCheckValue = undefined;
        }
        
        this.details.upsert({
          id: passportId,
          loadNumber: cachedData.loadSID || cachedData.loadNumber || this.loadNumber || '',
          dateTime: cachedData.dueDateTime || cachedData.scheduledDateTime || cachedData.loadingDateTime || '',
          dueDateTime: cachedData.dueDateTime || cachedData.scheduledDateTime || '',
          plannedDateTime: cachedData.plannedDateTime || this.plannedDateTime || '',
          vendorName: cachedData.vendorName || this.vendorName || '',
          vendorNumber: cachedData.vendorNumber || this.vendorNumber || '',
          apn: cachedData.apn || this.apn || '',
          apnName: cachedData.apnName || this.apnName || '',
          variety: cachedData.variety || '',
          haulier: cachedData.haulierName || cachedData.haulier || '',
          destination: cachedData.destination || this.destination || '',
          origin: cachedData.originName || cachedData.origin || this.origin || '',
          loadingDateTime: cachedData.loadingDateTime || '',
          bulkerNumber: cachedData.bulkerNumber || '',
          bulkerMain: cachedData.bulkerMain || '',
          haccpCheck: haccpCheckValue,
          haccpImages: cachedData.haccpImages || [],
          haccpImageLinks: cachedData.haccpImageLinks || [],
          authorisedBy: cachedData.authorizerName || cachedData.authorisedBy || '',
          plantID: cachedData.plantID || cachedData.PlantID,
          comments: cachedComments || [],
          history: cachedHistory || []
        });
        
        console.log('✅ Successfully loaded passport details from offline cache');
      } else {
        console.warn('⚠️ No cached data found for passport', passportId);
        // Use minimal data from component props
        this.useMinimalFallbackData(passportId);
      }
    } catch (error) {
      console.warn('❌ Failed to load from offline cache:', error);
      // Use minimal data from component props
      this.useMinimalFallbackData(passportId);
    }
  }

  /**
   * Use minimal fallback data from component props
   */
  private useMinimalFallbackData(passportId: string): void {
      this.details.upsert({
        id: passportId,
        loadNumber: this.loadNumber,
        origin: this.origin,
        destination: this.destination,
        vendorName: this.vendorName,
        vendorNumber: this.vendorNumber,
      apn: this.apn,
        apnName: this.apnName,
        plannedDateTime: this.plannedDateTime,
      });
  }

  async openComments(): Promise<void> {
    const id = this.recordId || this.loadNumber || '';
    
    // Always fetch/load passport details (handles online/offline automatically)
    // This ensures comments and history data are loaded from cache or API
    await this.fetchAndStorePassportDetails(id);
    
    const header = {
      loadNumber: this.loadNumber,
      tags: this.tags,
      variety: this.tags?.[0],
      haulier: this.tags?.[1],
      scheduledText: this.scheduledText,
      origin: this.origin,
      destination: this.destination,
      apnName: this.apnName,
      vendorName: this.vendorName,
      vendorNumber: this.vendorNumber,
      bulkerNumber: this.bulkerNumber
    };
    
    // Check if desktop view (window width >= 768px)
    const isDesktop = globalThis.window !== undefined && globalThis.window.innerWidth >= 768;
    
    if (isDesktop) {
      // Use service to open comments panel for desktop
      this.commentsPanelService.openCommentsPanel(id, header);
    } else {
      // Save scroll position and set returning flag before navigating (mobile only)
      this.scrollPositionService.saveScrollPosition('/growers');
      this.scrollPositionService.setReturningFromDetail(true);
      // Navigate to comments page for mobile
      void this.router.navigate(['/comments'], { state: { passportId: id, header } });
    }
  }

  async openHistory(): Promise<void> {
    const id = this.recordId || this.loadNumber || '';
    
    // Always fetch/load passport details (handles online/offline automatically)
    // This ensures comments and history data are loaded from cache or API
    await this.fetchAndStorePassportDetails(id);
    
    const header = {
      loadNumber: this.loadNumber,
      variety: this.tags?.[0],
      haulier: this.tags?.[1],
      scheduledText: this.scheduledText,
      origin: this.origin,
      destination: this.destination,
      apnName: this.apnName,
      vendorName: this.vendorName,
      vendorNumber: this.vendorNumber,
      bulkerNumber: this.bulkerNumber
    };
    
    // Check if desktop view (window width >= 768px)
    const isDesktop = globalThis.window !== undefined && globalThis.window.innerWidth >= 768;
    
    if (isDesktop) {
      // Use service to open history panel for desktop
      this.historyPanelService.openHistoryPanel(id, header);
    } else {
      // Save scroll position and set returning flag before navigating (mobile only)
      this.scrollPositionService.saveScrollPosition('/growers');
      this.scrollPositionService.setReturningFromDetail(true);
      // Navigate to history page for mobile
      void this.router.navigate(['/history'], { state: { passportId: id, header } });
    }
  }

  togglePlannedOpen(): void {
    this.plannedOpen = !this.plannedOpen;
  }

  selectPlanned(value: string): void {
    this.plannedLoadingValue = value;
    this.plannedOpen = false;
  }

  showPlannedInfo(): void {
    this.infoVisible = true;
    if (this.infoTimer) clearTimeout(this.infoTimer);
    this.infoTimer = setTimeout(() => {
      this.infoVisible = false;
    }, 3000);
  }

  openPlannedDatePicker(): void {
    // Open the datetime-local input picker
    const input = this.dateInputRef?.nativeElement;
    if (!input) return;
    
    // Chromium-based browsers support showPicker() for date/time inputs
    const anyInput = input as any;
    if (typeof anyInput.showPicker === 'function') {
      anyInput.showPicker();
      return;
    }
    // Fallback: focus + click usually opens the picker
    input.focus();
    input.click();
  }

  // Not needed anymore; input covers the button area for better iOS support

  onPlannedChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const rawValue = input.value;
    
    this.plannedLoadingISO = rawValue;
    
    if (rawValue) {
      // Beautify display using locale; fallback to raw value
      // datetime-local input gives local time, so Date() parses it as local
      try {
        const dt = new Date(rawValue);
        const mins = dt.getMinutes();
        const rounded = Math.round(mins / 5) * 5;
        if (mins !== rounded) {
          dt.setMinutes(rounded, 0, 0);
          const y = dt.getFullYear();
          const mo = String(dt.getMonth() + 1).padStart(2, '0');
          const d = String(dt.getDate()).padStart(2, '0');
          const h = String(dt.getHours()).padStart(2, '0');
          const mi = String(dt.getMinutes()).padStart(2, '0');
          this.plannedLoadingISO = `${y}-${mo}-${d}T${h}:${mi}`;
          input.value = this.plannedLoadingISO;
        }
        const datePart = dt.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
        const timePart = dt.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
        // Add ordinal suffix to date part
        const datePartWithOrdinal = this.dateTimeHelper.addOrdinalToDateString(datePart);
        this.plannedLoadingValue = `${datePartWithOrdinal}, ${timePart}`;
        
        // Persist change to store and backend (convert local datetime to ISO UTC)
        const id = this.recordId || this.loadNumber || '';
        const isoUtc = dt.toISOString();
        if (id && isoUtc) {
          this.details.update(id, { plannedDateTime: isoUtc });
          void this.persistPlannedDateTime(isoUtc);
        }
      } catch {
        this.plannedLoadingValue = rawValue;
      }
    } else {
      this.plannedLoadingValue = 'Select';
      const id = this.recordId || this.loadNumber || '';
      if (id) {
        this.details.update(id, { plannedDateTime: '' });
        // Send default old date instead of empty to indicate cleared
        void this.persistPlannedDateTime(this.dateTimeHelper.getClearedPlannedDate());
      }
    }
  }

  private toInputLocal(iso: string): string {
    return this.dateTimeHelper.convertUtcToLocal(iso);
  }

  /**
   * Get the minimum date/time for the planned date picker (current date/time)
   * Delegates to shared DateTimeHelperService
   */

  private formatDisplay(iso: string): string {
    return this.dateTimeHelper.formatScheduledText(iso) || iso;
  }

  private getRoleIdForPersona(persona: string): number {
    const roleMap: Record<string, number> = {
      'admin': 1,
      'grower': 2,
      'transloader': 3,
      'receiver': 4,
      'shunter': 5,
      'shunting': 5,
      'scheduler': 6,
      'gate house': 7,
      'gatehouse': 7
    };
    const normalized = (persona || '').toLowerCase();
    if (roleMap[normalized]) return roleMap[normalized];
    if (normalized.includes('admin')) return 1;
    if (normalized.includes('grower')) return 2;
    if (normalized.includes('transload')) return 3;
    if (normalized.includes('receiver')) return 4;
    if (normalized.includes('shunt')) return 5;
    if (normalized.includes('schedule')) return 6;
    if (normalized.includes('gate')) return 7;
    return 2;
  }

  /**
   * Get the persona name for this component
   * Always returns 'grower' since this component is only used in growers context
   */
  private get personaName(): string {
    return 'grower';
  }

  /** Reload planned date from API after server rejects update (e.g. load invoiced). */
  private async reloadGrowerPlannedFromApi(): Promise<void> {
    const passportId = this.recordId || this.loadNumber || '';
    if (!passportId) return;
    const token = localStorage.getItem('accessToken');
    const tokenType = localStorage.getItem('tokenType') || 'Bearer';
    if (!token) return;
    const apiUrl = `${environment.apiBaseUrl}/api/v1/passports/${passportId}/role/2`;
    const headers = { Authorization: `${tokenType} ${token}` };
    const response = await firstValueFrom(
      this.http.get<{ plannedDateTime?: string }>(apiUrl, { headers, withCredentials: true }),
    );
    const plannedRaw = response.plannedDateTime ?? '';
    const planned = plannedRaw ? this.dateTimeHelper.ensureUtcTimestamp(plannedRaw) : '';
    this.details.update(passportId, { plannedDateTime: planned });
    if (this.dateTimeHelper.isPlannedDateCleared(planned)) {
      this.plannedLoadingISO = '';
      this.plannedLoadingValue = 'Select';
    } else {
      this.plannedLoadingISO = this.toInputLocal(planned);
      this.plannedLoadingValue = this.formatDisplay(planned);
    }
  }

  private async persistPlannedDateTime(plannedIsoUtc: string): Promise<void> {
    try {
      const token = localStorage.getItem('accessToken');
      const tokenType = localStorage.getItem('tokenType') || 'Bearer';
      // FIX: Use component's personaName instead of localStorage to ensure correct roleId
      const persona = this.personaName; // Always use 'grower' for passport-cell component
      const roleId = this.getRoleIdForPersona(persona);
      const passportId = this.recordId || this.loadNumber || '';
      if (!token || !passportId) return;

      const apiUrl = `${environment.apiBaseUrl}/api/v1/passports/update/${passportId}/role/${roleId}`;
      const headers = {
        'Authorization': `${tokenType} ${token}`
        // Do NOT set Content-Type; let the browser set multipart/form-data boundary
      } as const;
      const body = new FormData();
      body.append('plannedDateTime', plannedIsoUtc);
      console.log('=== PLANNED LOADING UPDATE ===');
      console.log('PassportID:', passportId);
      console.log('RoleID:', roleId);
      console.log('Sending plannedDateTime:', plannedIsoUtc);
      console.log('Request body:', JSON.stringify(body));
      
      const updateResponse = await firstValueFrom(
        this.http.put<any>(apiUrl, body, { headers, withCredentials: true }),
      );
      console.log('PlannedDateTime update response:', updateResponse);
      
      // Use the value we just sent (already confirmed by backend)
      // No need to fetch the entire passport again
      // Check if this was a "cleared" date (sentinel value)
      if (this.dateTimeHelper.isPlannedDateCleared(plannedIsoUtc)) {
        // Value was cleared, reflect that in UI
        this.details.update(passportId, { plannedDateTime: '' });
        this.plannedLoadingISO = '';
        this.plannedLoadingValue = 'Select';
      } else {
        this.details.update(passportId, { plannedDateTime: plannedIsoUtc });
        this.plannedLoadingISO = this.toInputLocal(plannedIsoUtc);
        this.plannedLoadingValue = this.formatDisplay(plannedIsoUtc);
      }
    } catch (err: unknown) {
      const httpErr = err as { status?: number; error?: { error?: string; message?: string } };
      if (httpErr.status === 400 && httpErr.error?.error === 'stale_invoiced_load') {
        this.toastService.warning(
          httpErr.error?.message ?? 'This load has been invoiced. Planned date was refreshed from the server.',
          7000,
        );
        await this.reloadGrowerPlannedFromApi();
        return;
      }
      console.warn('Failed to persist plannedDateTime:', err);
    }
  }

  /**
   * Safely load comments from IndexedDB with error handling
   * Returns empty array on error instead of throwing
   */
  private async safeLoadComments(passportId: number): Promise<any[]> {
    try {
      return await this.offlineStorage.getComments(passportId);
    } catch (error: any) {
      console.warn(`Failed to load comments for passport ${passportId}:`, error);
      return [];
    }
  }

  /**
   * Safely load history from IndexedDB with error handling
   * Returns empty array on error instead of throwing
   */
  private async safeLoadHistory(passportId: number): Promise<any[]> {
    try {
      return await this.offlineStorage.getHistory(passportId);
    } catch (error: any) {
      console.warn(`Failed to load history for passport ${passportId}:`, error);
      return [];
    }
  }
}


